import type { Experience } from '@buakei/types';
import { Badge } from '@buakei/ui';
import { ShieldCheck } from 'lucide-react';
import { useId } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/auth/session';
import { EXPERIENCE_META } from './nav-config';

type Option = { value: string; label: string; experience: Experience; workspaceId?: string };

/** Lists only the experiences and workspaces this account holds (ADR-004 E). */
export function WorkspaceSwitcher({ current }: { current: Experience }) {
  const { session, setActiveWorkspace } = useAuth();
  const navigate = useNavigate();
  const id = useId();
  if (!session) return null;

  const options: Option[] = [];
  for (const experience of session.experiences) {
    if (experience === 'professional') {
      for (const ws of session.workspaces.filter((w) => w.kind !== 'learning')) {
        options.push({
          value: `professional:${ws.id}`,
          label: `Professional — ${ws.name}`,
          experience,
          workspaceId: ws.id,
        });
      }
    } else {
      options.push({ value: experience, label: EXPERIENCE_META[experience].label, experience });
    }
  }

  const currentValue =
    current === 'professional' ? `professional:${session.activeWorkspaceId ?? ''}` : current;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <label htmlFor={id} className="sr-only">
        Workspace
      </label>
      <select
        id={id}
        value={currentValue}
        onChange={(event) => {
          const option = options.find((o) => o.value === event.target.value);
          if (!option) return;
          if (option.workspaceId) setActiveWorkspace(option.workspaceId);
          void navigate(EXPERIENCE_META[option.experience].home);
        }}
        className="h-9 min-w-0 max-w-[16rem] truncate rounded-md border border-line-200 bg-white px-2 text-sm font-medium text-navy-900"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Privacy scope is visible before any request (DESIGN-SYSTEM rule 6). */
export function PrivacyScopeIndicator({ current }: { current: Experience }) {
  const { session } = useAuth();
  const workspace = session?.workspaces.find((w) => w.id === session.activeWorkspaceId);
  const label =
    current === 'professional' && workspace
      ? workspace.kind === 'firm'
        ? `Firm scope: ${workspace.name}`
        : 'Personal scope'
      : EXPERIENCE_META[current].scope;
  return (
    <Badge
      tone="plain"
      className="hidden text-navy-900 md:inline-flex"
      aria-label={`Privacy scope: ${label}`}
    >
      <ShieldCheck aria-hidden />
      {label}
    </Badge>
  );
}
